import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './TestTaking.css';

const TestTaking = ({ courseId, weekNo, studentId, onTestComplete }) => {
  const [test, setTest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [testStarted, setTestStarted] = useState(false);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [timeRemaining, setTimeRemaining] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [result, setResult] = useState(null);
  const [testGate, setTestGate] = useState(null);
  const [toast, setToast] = useState(null);
  const [cooldownSeconds, setCooldownSeconds] = useState(0);

  useEffect(() => {
    loadTest();
  }, [weekNo]);

  useEffect(() => {
    if (testStarted && timeRemaining > 0) {
      const timer = setTimeout(() => setTimeRemaining(timeRemaining - 1), 1000);
      return () => clearTimeout(timer);
    } else if (timeRemaining === 0 && testStarted && !submitted) {
      handleSubmitTest();
    }
  }, [timeRemaining, testStarted, submitted]);

  // Cooldown countdown timer
  useEffect(() => {
    if (cooldownSeconds > 0) {
      const timer = setTimeout(() => setCooldownSeconds(cooldownSeconds - 1), 1000);
      return () => clearTimeout(timer);
    } else if (cooldownSeconds === 0 && testGate && !testGate.can_attempt && testGate.reason === 'cooldown') {
      // Cooldown expired, refresh gate state
      loadTest();
    }
  }, [cooldownSeconds]);

  const loadTest = async () => {
    try {
      setLoading(true);
      setSubmitted(false);
      setResult(null);
      setTestGate(null);

      // Get test for week
      const testsRes = await axios.get(`/api/tests/by-course/${courseId}`);
      const weekTest = testsRes.data.find(t => t.week_no === weekNo);

      if (weekTest) {
        setTest(weekTest);

        // Check attempt history + gate state in a single call
        try {
          const attemptRes = await axios.get(
            `/api/tests/attempt/${studentId}/${weekTest.test_id}`
          );
          const data = attemptRes.data;

          // Only show results if student has actually attempted the test
          if (data && data.attempted === true && data.has_submitted_attempt === true) {
            setResult(data);
            setSubmitted(true);
          }

          // Set gate state from attempt response
          if (data) {
            const canTake = data.can_take_test !== false && data.can_retake !== false;
            const reason = data.block_reason || null;
            const retrySeconds = data.retry_after_seconds || 0;

            if (!canTake && reason) {
              setTestGate({
                can_attempt: false,
                reason: reason,
                retry_after_seconds: retrySeconds,
                attempts_used: data.attempts_used || 0
              });
              if (reason === 'cooldown' && retrySeconds > 0) {
                setCooldownSeconds(retrySeconds);
              }
            } else {
              setTestGate({
                can_attempt: true,
                reason: null,
                retry_after_seconds: 0,
                attempts_used: data.attempts_used || 0
              });
            }
          }
        } catch (attemptErr) {
          console.error('Failed to load attempt data:', attemptErr);
        }
      }
    } catch (error) {
      console.error('Failed to load test:', error);
      setToast({ type: 'error', message: 'Failed to load test' });
    } finally {
      setLoading(false);
    }
  };

  const handleStartTest = async () => {
    try {
      await axios.post('/api/tests/start', {
        student_id: studentId,
        test_id: test.test_id
      });

      const durationMinutes = test.duration_minutes || 30;
      setTimeRemaining(durationMinutes * 60);
      setTestStarted(true);
      setSubmitted(false);
      setResult(null);
      setAnswers({});
      setCurrentQuestionIndex(0);
    } catch (error) {
      if (error.response?.status === 403 && error.response?.data?.status === 'cooldown') {
        const secs = error.response.data.retry_after_seconds || 300;
        setCooldownSeconds(secs);
        setTestGate({
          can_attempt: false,
          reason: 'cooldown',
          retry_after_seconds: secs,
          attempts_used: error.response.data.attempts_used || 0
        });
        setToast({ type: 'info', message: `Please wait ${Math.ceil(secs / 60)} minutes before retrying` });
      } else {
        setToast({ type: 'error', message: error.response?.data?.error || 'Failed to start test' });
      }
    }
  };

  const handleAnswerChange = (questionIndex, selectedOption) => {
    setAnswers({
      ...answers,
      [questionIndex]: selectedOption
    });
  };

  const handleSubmitTest = async () => {
    try {
      // Convert answers from {index: option} to [{question_no, selected_option}]
      const answersArray = Object.entries(answers).map(([idx, option]) => ({
        question_no: parseInt(idx) + 1,  // Backend uses 1-based question numbers
        selected_option: option
      }));

      const response = await axios.post('/api/tests/submit', {
        student_id: studentId,
        test_id: test.test_id,
        answers: answersArray
      });

      setResult(response.data);
      setSubmitted(true);
      setTestStarted(false);

      if (response.data.status === 'passed') {
        setToast({ type: 'success', message: 'Congratulations! You passed!' });
        setTestGate({ can_attempt: false, reason: 'passed', retry_after_seconds: 0 });
      } else {
        setToast({ type: 'info', message: 'You did not pass. You can retry after the cooldown period.' });
        // Set cooldown (5 minutes)
        setCooldownSeconds(300);
        setTestGate({
          can_attempt: false,
          reason: 'cooldown',
          retry_after_seconds: 300,
          attempts_used: response.data.attempt_number || 1
        });
      }

      onTestComplete?.(response.data);
    } catch (error) {
      setToast({ type: 'error', message: error.response?.data?.error || 'Failed to submit test' });
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
  };

  if (loading) return <div className="test-loader">Loading test...</div>;

  if (!test) {
    return <div className="no-test">No test available for this week</div>;
  }

  const questionsData = typeof test.questions === 'string' 
    ? JSON.parse(test.questions) 
    : test.questions;
  const questions = Array.isArray(questionsData) ? questionsData : [];

  // Check gate state — show only if NOT submitted yet (no previous result to show)
  if (testGate && !testGate.can_attempt && !submitted) {
    return (
      <div className="test-container">
        <div className="test-gate-card">
          {testGate.reason === 'passed' ? (
            <>
              <h3>🎉 Test Passed!</h3>
              <p>You have already passed this test. Great work!</p>
            </>
          ) : testGate.reason === 'max_attempts' ? (
            <>
              <h3>🚫 No More Attempts</h3>
              <p>You have exhausted the maximum number of attempts for this test.</p>
            </>
          ) : testGate.reason === 'cooldown' ? (
            <>
              <h3>⏳ Cooldown Period</h3>
              <p>You must wait before attempting again.</p>
              <p className="cooldown-time">
                Retry available in: <strong>{formatTime(cooldownSeconds)}</strong>
              </p>
            </>
          ) : (
            <>
              <h3>🚫 Test Unavailable</h3>
              <p>You cannot take this test right now.</p>
            </>
          )}
        </div>
        {toast && (
          <div className={`toast toast-${toast.type}`}>
            <span>{toast.message}</span>
            <button className="toast-close" onClick={() => setToast(null)}>×</button>
          </div>
        )}
      </div>
    );
  }

  // Show results if student has actually submitted an attempt
  if (submitted && result && result.attempted !== false) {
    return (
      <div className="test-container">
        <div className={`test-result test-${result.status}`}>
          <div className="result-header">
            <h2>
              {result.status === 'passed' ? '🎉 Test Passed!' : '📊 Test Result'}
            </h2>
          </div>

          <div className="result-stats">
            <div className="stat">
              <div className="stat-label">Score</div>
              <div className="stat-value">{result.score}/{result.total_marks}</div>
            </div>
            <div className="stat">
              <div className="stat-label">Percentage</div>
              <div className="stat-value">{result.percentage}%</div>
            </div>
            <div className="stat">
              <div className="stat-label">Status</div>
              <div className={`stat-value status-${result.status}`}>
                {result.status === 'passed' ? '✓ PASSED' : '✕ FAILED'}
              </div>
            </div>
          </div>

          {result.status === 'failed' && cooldownSeconds > 0 && (
            <div className="cooldown-notice">
              <p>⏳ You can retry in: <strong>{formatTime(cooldownSeconds)}</strong></p>
            </div>
          )}

          {result.status === 'failed' && cooldownSeconds <= 0 && (
            testGate?.can_attempt !== false ? (
              <button className="btn-primary btn-retry" onClick={handleStartTest}>
                🔄 Retry Test
              </button>
            ) : testGate?.reason === 'max_attempts' ? (
              <div className="cooldown-notice"><p>No more attempts available.</p></div>
            ) : null
          )}

          {result.attempts_used != null && (
            <div className="attempt-info">
              <span>Attempt {result.attempt_number || result.attempts_used} of {result.max_attempts || 4}</span>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Test interface
  if (testStarted && questions.length > 0) {
    const currentQuestion = questions[currentQuestionIndex];

    return (
      <div className="test-container">
        <div className="test-interface">
          {/* Header with Timer */}
          <div className="test-header">
            <div className="test-title">
              <h3>{test.title}</h3>
            </div>
            <div className={`timer ${timeRemaining < 300 ? 'warning' : ''}`}>
              ⏱ {formatTime(timeRemaining)}
            </div>
          </div>

          {/* Progress */}
          <div className="test-progress">
            <div className="progress-bar">
              <div
                className="progress-fill"
                style={{ width: `${((currentQuestionIndex + 1) / questions.length) * 100}%` }}
              />
            </div>
            <span className="progress-text">
              Question {currentQuestionIndex + 1} of {questions.length}
            </span>
          </div>

          {/* Question */}
          <div className="question-area">
            <h4 className="question-text">{currentQuestion.question}</h4>

            <div className="options-container">
              {currentQuestion.options?.map((option, idx) => (
                <label key={idx} className="option-label">
                  <input
                    type="radio"
                    name={`question-${currentQuestionIndex}`}
                    value={option}
                    checked={answers[currentQuestionIndex] === option}
                    onChange={() => handleAnswerChange(currentQuestionIndex, option)}
                  />
                  <span className="option-text">{option}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Navigation */}
          <div className="test-navigation">
            <button
              className="btn-secondary"
              disabled={currentQuestionIndex === 0}
              onClick={() => setCurrentQuestionIndex(currentQuestionIndex - 1)}
            >
              ← Previous
            </button>

            <div className="question-counter">
              {Array.from({ length: questions.length }, (_, i) => (
                <button
                  key={i}
                  className={`counter-btn ${i === currentQuestionIndex ? 'current' : ''} ${answers[i] ? 'answered' : ''}`}
                  onClick={() => setCurrentQuestionIndex(i)}
                >
                  {i + 1}
                </button>
              ))}
            </div>

            {currentQuestionIndex === questions.length - 1 ? (
              <button
                className="btn-primary"
                onClick={handleSubmitTest}
              >
                Submit Test ✓
              </button>
            ) : (
              <button
                className="btn-secondary"
                onClick={() => setCurrentQuestionIndex(currentQuestionIndex + 1)}
              >
                Next →
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Starting screen
  return (
    <div className="test-container">
      <div className="test-start-card">
        <h3>📝 {test.title}</h3>
        <div className="test-info">
          <div className="info-item">
            <span className="icon">❓</span>
            <span>{questions.length} Questions</span>
          </div>
          <div className="info-item">
            <span className="icon">⏱</span>
            <span>{test.duration_minutes} Minutes</span>
          </div>
          <div className="info-item">
            <span className="icon">✓</span>
            <span>Pass Score: 50%</span>
          </div>
        </div>

        <div className="test-rules">
          <h4>Test Rules:</h4>
          <ul>
            <li>Answer all questions within the time limit</li>
            <li>You cannot pause the test</li>
            <li>Minimum 50% score is required to pass</li>
            <li>You can attempt this test up to 4 times</li>
          </ul>
        </div>

        <button className="btn-primary btn-start" onClick={handleStartTest}>
          Start Test →
        </button>
      </div>

      {toast && (
        <div className={`toast toast-${toast.type}`}>
          <span>{toast.message}</span>
          <button className="toast-close" onClick={() => setToast(null)}>×</button>
        </div>
      )}
    </div>
  );
};

export default TestTaking;
